import {
  faBoxTaped,
  faCreditCardAlt,
  faGiftCard,
} from '@fortawesome/pro-solid-svg-icons';
import Alert from '~/components/Alert';
import LinkButton from '~/components/button/LinkButton';
import CardSection from '~/components/CardSection';
import Divide from '~/components/Divide';
import Page from '~/components/layout/page/Page';
import LineItem from '~/components/LineItem';
import PaginationControls from '~/components/pagination/PaginationControls';
import usePagination from '~/components/pagination/usePagination';
import { ClaimRollupStatuses } from '~/utils/constants';
import { useCurrencyFormatter } from '~/utils/formatters';
import iconToDataUri from '~/utils/iconToDataUri';
import Chip from './Chip';
import ClaimLineItem from './ClaimLineItem';
import { useClaimOverviewData } from './claimOverview';
import { HappyReturnQrCode } from './HappyReturnQrCode';
import { QrCode } from './QrCode';

function LineItemChips({ chips }: { chips: string[] }) {
  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="flex gap-1">
      {chips.map((chip) => (
        <Chip key={chip}>{chip}</Chip>
      ))}
    </div>
  );
}

function ResolutionSummary() {
  const { resolutionSummary } = useClaimOverviewData();
  const currencyFormatter = useCurrencyFormatter();

  if (
    !resolutionSummary?.exchangeOrders &&
    !resolutionSummary?.refundsTotal &&
    !resolutionSummary?.giftCardsTotal
  ) {
    return null;
  }

  const { exchangeOrders, refundsTotal, giftCardsTotal } = resolutionSummary;

  const refundChips = [];

  if (refundsTotal && refundsTotal.shippingFee > 0) {
    refundChips.push(
      `Return Shipping Fee: -${currencyFormatter(refundsTotal.shippingFee)}`,
    );
  }

  if (refundsTotal && refundsTotal.handlingFee > 0) {
    refundChips.push(
      `Handling Fee: -${currencyFormatter(refundsTotal.handlingFee)}`,
    );
  }

  const giftCardChips = [];

  if (giftCardsTotal && giftCardsTotal.incentiveAmount > 0) {
    giftCardChips.push(
      `Incentive: +${currencyFormatter(giftCardsTotal.incentiveAmount)}`,
    );
  }

  return (
    <Page.Section title="What you are getting">
      <Divide>
        {exchangeOrders?.map((exchangeOrder) => (
          <div key={exchangeOrder.id} className="space-y-4">
            <LineItem
              name={`New Order: ${exchangeOrder.orderNameFromPlatform}`}
              imgSrc={iconToDataUri(faBoxTaped)}
              variant="small"
            />
            <div className="space-y-4 pl-4">
              {exchangeOrder.lineItems.map((item) => (
                <LineItem
                  key={item.id}
                  name={item.name ?? 'n/a'}
                  options={item.optionsFromPlatform}
                  imgSrc={item.imgUrl}
                  variant="small"
                  quantity={item.quantity}
                />
              ))}
            </div>
          </div>
        ))}

        {refundsTotal && (
          <LineItem
            name="Refund To Original Payment Method"
            imgSrc={iconToDataUri(faCreditCardAlt)}
            variant="small"
            price={refundsTotal.amount}
            footnote={<LineItemChips chips={refundChips} />}
          />
        )}

        {giftCardsTotal && (
          <LineItem
            name="Gift Card"
            imgSrc={iconToDataUri(faGiftCard)}
            variant="small"
            price={giftCardsTotal.amount}
            footnote={<LineItemChips chips={giftCardChips} />}
          />
        )}
      </Divide>
    </Page.Section>
  );
}

function RequestSummary() {
  const { requestSummary } = useClaimOverviewData();

  if (!requestSummary) return null;

  const {
    refundTotal,
    giftCardTotal,
    variantExchangeLineItems,
    giftCardChips,
    refundChips,
    showRefund,
    showGiftCard,
    originatingOrderCreditAlert,
  } = requestSummary;

  if (!variantExchangeLineItems.length && !showRefund && !showGiftCard)
    return null;

  return (
    <Page.Section title="What you are getting">
      {originatingOrderCreditAlert && (
        <Alert variant="info" title={originatingOrderCreditAlert.title}>
          {originatingOrderCreditAlert.detail}
        </Alert>
      )}
      <Divide>
        {variantExchangeLineItems.map((item) => (
          <LineItem
            key={item.idFromPlatform}
            name={item.title ?? 'No Title'}
            options={item.options}
            imgSrc={item.imgUrl}
            variant="small"
            quantity={item.quantity}
          />
        ))}
        {showRefund && (
          <LineItem
            name="Refund To Original Payment Method"
            imgSrc={iconToDataUri(faCreditCardAlt)}
            variant="small"
            price={refundTotal}
            footnote={<LineItemChips chips={refundChips} />}
          />
        )}
        {showGiftCard && (
          <LineItem
            name="Gift Card"
            imgSrc={iconToDataUri(faGiftCard)}
            variant="small"
            price={giftCardTotal}
            footnote={<LineItemChips chips={giftCardChips} />}
          />
        )}
      </Divide>
    </Page.Section>
  );
}

function GreenReturnItems() {
  const {
    greenReturns: { items, title, subtitle },
  } = useClaimOverviewData();

  if (items.length === 0) {
    return null;
  }

  return (
    <Page.Section title={title} subtitle={subtitle}>
      <Divide>
        {items.map((item) => (
          <ClaimLineItem
            key={item.id}
            name={item.originalStoreOrderLineItem.name ?? 'n/a'}
            imgUrl={item.originalStoreOrderLineItem.imgUrl}
            quantity={item.quantity}
            price={item.cost}
            productOptions={item.originalStoreOrderLineItem.optionsFromPlatform}
            requestedResolution={item.requestedResolutionMethodEnum}
            claimLineItemNoteToCustomer={item.noteToCustomer}
            fees={item.fees}
          />
        ))}
      </Divide>
    </Page.Section>
  );
}

type Shipment = ReturnType<
  typeof useClaimOverviewData
>['returnShipments'][number];

type Overview = ReturnType<typeof useClaimOverviewData>;

function LabelOrPackingSlipShipment({
  overview,
  shipment,
  existsInOtherShipments: iExistInOtherShipments,
}: {
  existsInOtherShipments: boolean;
  overview: Omit<Overview, 'returnShipments'>;
  shipment:
    | Extract<Shipment, { returnShipmentType: 'Label' }>
    | Extract<Shipment, { returnShipmentType: 'Packing_Slip' }>;
}) {
  const {
    claimExternalId,
    storeUrl,
    hideLabelDownloadWhenQrCodeIsPresent,
    isEmbedded,
    isCancelled,
  } = overview;

  const {
    isExpired,
    returnShipmentPdfAssetUrl,
    downloadLabel,
    fromAddress,
    customInstructions,
  } = shipment;

  const qrCodeDetail =
    shipment.returnShipmentType === 'Label' ? shipment.qrCodeDetail : undefined;

  const shouldHideLabelDownload =
    hideLabelDownloadWhenQrCodeIsPresent && qrCodeDetail;

  return (
    <>
      {iExistInOtherShipments && customInstructions?.parcelName && (
        <Alert
          title="This item is shipped back with multiple shipments"
          variant="info"
        >
          {customInstructions.parcelName}
        </Alert>
      )}

      {!isExpired && !isCancelled && (
        <>
          {qrCodeDetail && (
            <QrCode
              details={{
                ...qrCodeDetail,
                confirmation: claimExternalId,
                fromAddress,
              }}
            />
          )}

          <div className="flex flex-col gap-4 md:flex-row">
            {returnShipmentPdfAssetUrl && !shouldHideLabelDownload && (
              <LinkButton
                className="w-full md:w-1/2"
                variant="outlined"
                to={returnShipmentPdfAssetUrl}
                target="_blank"
              >
                {downloadLabel}
              </LinkButton>
            )}
            {storeUrl && !isEmbedded && !customInstructions?.fileUrl && (
              <LinkButton
                className="w-full md:w-1/2"
                to={storeUrl}
                variant="outlined"
                target="_blank"
                rel="noreferrer"
              >
                Back To Our Website
              </LinkButton>
            )}
            {customInstructions?.fileUrl && (
              <LinkButton
                className="w-full md:w-1/2"
                to={customInstructions.fileUrl}
                variant="filled"
                target="_blank"
                rel="noreferrer"
              >
                {customInstructions.fileUrlCta ??
                  'Download Packing Instructions'}
              </LinkButton>
            )}
          </div>
        </>
      )}
    </>
  );
}

function ReturnShipments() {
  const overviewData = useClaimOverviewData();
  const { returnShipments, claimRollup } = overviewData;
  const [page, pagination] = usePagination({
    items: returnShipments,
    itemsPerPage: 1,
  });

  const shipment = page[0];

  if (!shipment) return null;

  const { subtitle, title, claimLineItems, returnShipmentType } = shipment;

  const isConsolidatedDropoff = returnShipmentType === 'Consolidated_Dropoff';
  const myCliIds = new Set(claimLineItems.map((cli) => cli.id));

  const iExistInOtherShipments = returnShipments.some(
    (rs) =>
      rs.returnShipmentId !== shipment.returnShipmentId &&
      rs.claimLineItems.some((cli) => myCliIds.has(cli.id)),
  );

  return (
    <Page.Section title={title} subtitle={subtitle}>
      <Divide>
        {claimLineItems.map(
          ({
            id,
            quantity,
            status,
            cost,
            originalStoreOrderLineItem: { name, imgUrl, optionsFromPlatform },
            requestedResolutionMethodEnum,
            noteToCustomer: claimLineItemNoteToCustomer,
            resolutionMethodEnum,
            fees,
          }) => (
            <ClaimLineItem
              key={id}
              name={name}
              className="mb-4 border-b border-outline pb-4 last:mb-0 last:border-none last:pb-0"
              price={cost}
              imgUrl={imgUrl}
              quantity={quantity}
              status={status.name}
              rollupStatus={claimRollup.code}
              requestedResolution={
                resolutionMethodEnum ?? requestedResolutionMethodEnum
              }
              claimLineItemNoteToCustomer={claimLineItemNoteToCustomer}
              productOptions={optionsFromPlatform}
              fees={fees}
            />
          ),
        )}
      </Divide>

      {isConsolidatedDropoff ? (
        <HappyReturnQrCode shipment={shipment} />
      ) : (
        <LabelOrPackingSlipShipment
          shipment={shipment}
          overview={overviewData}
          existsInOtherShipments={iExistInOtherShipments}
        />
      )}

      <PaginationControls
        // TODO: get feedback on this positioning
        loop
        className="justify-center"
        // oxlint-disable-next-line react/jsx-props-no-spreading -- pagination controls props are the same as pagination object
        {...pagination}
      />
    </Page.Section>
  );
}

// TODO: revisit this page styles for the page sections changes
export default function ClaimOverviewPage() {
  const { claimExternalId, isCompleted, noteToCustomer, claimRollup } =
    useClaimOverviewData();

  return (
    <Page.Root title="Claim Overview" hideHeader>
      <Page.Section title={`Confirmation #${claimExternalId}`}>
        <CardSection
          title="Status"
          description={ClaimRollupStatuses[claimRollup.code]}
        />

        {noteToCustomer && isCompleted && (
          <CardSection title="Note to Customer" description={noteToCustomer} />
        )}
      </Page.Section>

      <ReturnShipments />
      <GreenReturnItems />

      {isCompleted ? <ResolutionSummary /> : <RequestSummary />}
    </Page.Root>
  );
}
